import {
  CATEGORIAS_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO,
  ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN,
  type DocumentoDeCumplimiento,
  type TipoDeDocumentoDeCumplimiento,
} from '@cnc/contracts';
import {useArchivoDeCumplimiento, useEditarDocumentoDeCumplimiento, useTiposDeCumplimiento} from '../api/cumplimiento';
import {et, tr} from '../i18n/idioma';
import {fecha, numero} from '../utilidades/formato';
import {AYUDA_ARCHIVO as AA} from './ayuda-cumplimiento';
import {CajaAjustable} from './caja-ajustable';
import {DescargaDeDocumento, FichaDeSituacion} from './cumplimiento-documento';
import {ARCHIVO_INICIAL, situacionDeArchivo, tamanoLegible, type FiltrosDeArchivo} from './cumplimiento-estilos';
import {NotaDeAyuda} from './nota-de-ayuda';
import {Paginacion} from './paginacion';
import {usePermisos} from './sesion-contexto';

// Cumplimiento → Archivo: todos los archivos como estaban en la carpeta de
// compliance, para encontrarlos. Se navega por carpetas o se busca por nombre;
// los filtros buscan en todas las carpetas.
export function ArchivoDeCumplimiento({
  filtros,
  onFiltros,
}: {
  filtros: FiltrosDeArchivo;
  onFiltros: (f: FiltrosDeArchivo) => void;
}) {
  const parametros = new URLSearchParams();
  if (filtros.carpeta) parametros.set('carpeta', filtros.carpeta);
  if (filtros.buscar.trim()) parametros.set('buscar', filtros.buscar.trim());
  if (filtros.categoria) parametros.set('categoria', filtros.categoria);
  if (filtros.tipo) parametros.set('tipo', filtros.tipo);
  if (filtros.vinculo) parametros.set('vinculo', filtros.vinculo);
  if (filtros.vencimiento === 'vencidos') parametros.set('soloVencidos', 'true');
  if (filtros.vencimiento === 'por_vencer') parametros.set('soloPorVencer', 'true');
  parametros.set('pagina', String(filtros.pagina));
  parametros.set('porPagina', String(filtros.porPagina));
  const archivo = useArchivoDeCumplimiento(parametros.toString());
  const tipos = useTiposDeCumplimiento();
  const categorias = et(ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO, ETIQUETA_CATEGORIA_DE_CUMPLIMIENTO_EN);
  const cambiar = (cambio: Partial<FiltrosDeArchivo>) => onFiltros({...filtros, pagina: 1, ...cambio});
  const a = archivo.data;
  const migas = filtros.carpeta ? filtros.carpeta.split('/') : [];
  const conFiltros = !!(
    filtros.buscar.trim() ||
    filtros.categoria ||
    filtros.tipo ||
    filtros.vinculo ||
    filtros.vencimiento
  );
  const selector = 'rounded-md border border-linea bg-white px-2 py-1.5 text-sm';
  return (
    <div className="space-y-3" data-prueba="cumplimiento-archivo">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={filtros.buscar}
          onChange={e => cambiar({buscar: e.target.value})}
          placeholder={tr('Buscar archivo por nombre o carpeta…', 'Search file by name or folder…')}
          aria-label={tr('Buscar archivo', 'Search file')}
          className="w-80 rounded-md border border-linea bg-white px-3 py-1.5 text-sm"
        />
        <NotaDeAyuda titulo={tr('Buscar archivo', 'Search file')} texto={AA.buscar} />
        <select
          aria-label={tr('Categoría', 'Category')}
          value={filtros.categoria}
          onChange={e => cambiar({categoria: e.target.value})}
          className={selector}
        >
          <option value="">{tr('Todas las categorías', 'All categories')}</option>
          {CATEGORIAS_DE_CUMPLIMIENTO.map(c => (
            <option key={c} value={c}>
              {categorias[c]}
            </option>
          ))}
        </select>
        <NotaDeAyuda titulo={tr('Categoría', 'Category')} texto={AA.categoria} />
        <select
          aria-label={tr('Tipo de documento', 'Document type')}
          value={filtros.tipo}
          onChange={e => cambiar({tipo: e.target.value})}
          className={selector}
        >
          <option value="">{tr('Todos los tipos', 'All types')}</option>
          <option value="sin_clasificar">{tr('— Sin clasificar —', '— Unclassified —')}</option>
          {(tipos.data?.tipos ?? [])
            .filter(t => !filtros.categoria || t.categoria === filtros.categoria)
            .map(t => (
              <option key={t.id} value={t.id}>
                {t.nombre} ({categorias[t.categoria]})
              </option>
            ))}
        </select>
        <NotaDeAyuda titulo={tr('Tipo de documento', 'Document type')} texto={AA.tipo} />
        <select
          aria-label={tr('Vencimiento', 'Expiry')}
          value={filtros.vencimiento}
          onChange={e => cambiar({vencimiento: e.target.value as FiltrosDeArchivo['vencimiento']})}
          className={selector}
        >
          <option value="">{tr('Cualquier vencimiento', 'Any expiry')}</option>
          <option value="vencidos">{tr('Solo vencidos', 'Expired only')}</option>
          <option value="por_vencer">{tr('Vencen en 60 días', 'Expire within 60 days')}</option>
        </select>
        <NotaDeAyuda titulo={tr('Vencimiento', 'Expiry')} texto={AA.vencimiento} />
        {(conFiltros || filtros.carpeta) && (
          <button
            type="button"
            onClick={() => onFiltros(ARCHIVO_INICIAL)}
            className="text-sm text-verde hover:underline"
          >
            {tr('✕ Quitar filtros', '✕ Clear filters')}
          </button>
        )}
      </div>

      <nav aria-label={tr('Carpeta abierta', 'Open folder')} className="flex flex-wrap items-center gap-1 text-sm">
        <button type="button" onClick={() => cambiar({carpeta: ''})} className="text-verde hover:underline">
          📁 Compliance
        </button>
        {migas.map((nombre, i) => (
          <span key={i} className="flex items-center gap-1">
            <span className="text-apagado">/</span>
            <button
              type="button"
              onClick={() => cambiar({carpeta: migas.slice(0, i + 1).join('/')})}
              className={i === migas.length - 1 ? 'font-medium' : 'text-verde hover:underline'}
            >
              {nombre}
            </button>
          </span>
        ))}
        {conFiltros && (
          <span className="ml-2 text-xs text-apagado">
            {tr('(buscando en esta carpeta y todas sus subcarpetas)', '(searching this folder and all its subfolders)')}
          </span>
        )}
      </nav>

      {archivo.isError && <p className="text-sm text-alerta">{archivo.error.message}</p>}
      <CajaAjustable altoMaximo="calc(100vh - 20rem)" className="overflow-auto rounded-lg border border-linea bg-white">
        {!a ? (
          <p className="p-3 text-sm text-apagado">{tr('Cargando…', 'Loading…')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="text-left text-xs tracking-wide text-apagado uppercase">
                <th className="px-3 py-2 font-semibold">
                  {tr('Nombre', 'Name')}
                  <NotaDeAyuda titulo={tr('Nombre', 'Name')} texto={AA.nombre} />
                </th>
                <th className="px-2 py-2 font-semibold">
                  {tr('Tipo', 'Type')}
                  <NotaDeAyuda titulo={tr('Tipo', 'Type')} texto={AA.tipoColumna} />
                </th>
                <th className="px-2 py-2 font-semibold">
                  {tr('Recibido', 'Received')}
                  <NotaDeAyuda titulo={tr('Recibido', 'Received')} texto={AA.recibido} />
                </th>
                <th className="px-2 py-2 font-semibold">
                  {tr('Vence', 'Expires')}
                  <NotaDeAyuda titulo={tr('Vence', 'Expires')} texto={AA.vence} />
                </th>
                <th className="px-2 py-2 font-semibold">
                  {tr('Situación', 'Status')}
                  <NotaDeAyuda titulo={tr('Situación', 'Status')} texto={AA.situacion} />
                </th>
              </tr>
            </thead>
            <tbody>
              {!conFiltros &&
                a.carpetas.map(c => (
                  <tr key={c.ruta} className="border-t border-linea/70 hover:bg-papel">
                    <td colSpan={5} className="px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => cambiar({carpeta: c.ruta})}
                        className="flex items-center gap-2 text-left"
                      >
                        <span aria-hidden>📁</span>
                        <span className="font-medium">{c.nombre}</span>
                        <span className="text-xs text-apagado">
                          {tr(`${numero(c.archivos)} archivos`, `${numero(c.archivos)} files`)}
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              {a.elementos.map(d => (
                <FilaDeArchivo key={d.id} d={d} hoy={a.hoy} tipos={tipos.data?.tipos ?? []} mostrarRuta={conFiltros} />
              ))}
              {!a.elementos.length && (conFiltros || !a.carpetas.length) && (
                <tr>
                  <td colSpan={5} className="p-3 text-sm text-apagado">
                    {conFiltros
                      ? tr('Ningún archivo con esos filtros.', 'No file matches those filters.')
                      : tr(
                          'Todavía no hay archivos. Cárgalos en la pestaña Cargar carpeta.',
                          'There are no files yet. Load them in the Load folder tab.',
                        )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </CajaAjustable>
      {a && a.totalPaginas > 1 && (
        <Paginacion
          pagina={a.pagina}
          totalPaginas={a.totalPaginas}
          porPagina={a.porPagina}
          total={a.total}
          onPagina={pagina => onFiltros({...filtros, pagina})}
          onPorPagina={porPagina => onFiltros({...filtros, pagina: 1, porPagina})}
          unidad={{es: 'archivos', en: 'files'}}
        />
      )}
    </div>
  );
}

function FilaDeArchivo({
  d,
  hoy,
  tipos,
  mostrarRuta,
}: {
  d: DocumentoDeCumplimiento;
  hoy: string;
  tipos: TipoDeDocumentoDeCumplimiento[];
  mostrarRuta: boolean;
}) {
  const permisos = usePermisos();
  const editar = useEditarDocumentoDeCumplimiento();
  const tipo = tipos.find(t => t.id === d.tipoId);
  const situacion = situacionDeArchivo(d, hoy, tipo?.diasDeAviso);
  const deLaCategoria = tipos.filter(t => t.categoria === d.categoria && t.activo);
  const carpeta = d.rutaOriginal?.split('/').slice(0, -1).join('/');
  // Sin archivo guardado (p. ej. "no aplica"), el nombre que tenia en la carpeta.
  const nombre = d.archivo ?? d.rutaOriginal?.split('/').at(-1) ?? d.tipo ?? tr('Documento', 'Document');
  return (
    <tr className="border-t border-linea/70 align-top hover:bg-papel" data-prueba="fila-archivo">
      <td className="px-3 py-1.5">
        <div className="break-all">
          <DescargaDeDocumento d={d} />
          {!d.archivo && <span>{nombre}</span>}
        </div>
        <div className="text-xs text-apagado">
          {d.vinculo}
          {d.subcarpeta && ` · ${d.subcarpeta}`}
          {d.tamanoBytes !== null && ` · ${tamanoLegible(d.tamanoBytes)}`}
        </div>
        {mostrarRuta && carpeta && <div className="text-xs break-all text-apagado">📁 {carpeta}</div>}
      </td>
      <td className="px-2 py-1.5">
        {permisos.editar && deLaCategoria.length > 0 && d.subcarpeta !== 'Formats' && d.subcarpeta !== 'Templates' ? (
          <select
            aria-label={tr(`Tipo de ${nombre}`, `Type of ${nombre}`)}
            value={d.tipoId ?? ''}
            disabled={editar.isPending}
            onChange={e =>
              editar.mutate({
                id: d.id,
                edicion: {version: d.version, tipoId: e.target.value ? Number(e.target.value) : null},
              })
            }
            className={`max-w-52 rounded-md border px-1 py-0.5 text-xs ${d.tipoId === null ? 'border-dorado bg-dorado-claro' : 'border-linea bg-white'}`}
          >
            <option value="">{tr('Sin clasificar', 'Unclassified')}</option>
            {deLaCategoria.map(t => (
              <option key={t.id} value={t.id}>
                {t.nombre}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-xs">
            {d.tipo ??
              (d.subcarpeta === 'Formats' || d.subcarpeta === 'Templates'
                ? tr('Formato', 'Form')
                : tr('Sin clasificar', 'Unclassified'))}
          </span>
        )}
        {editar.isError && <div className="text-xs text-alerta">{editar.error.message}</div>}
      </td>
      <td className="px-2 py-1.5 text-xs whitespace-nowrap">{d.recibidoEl ? fecha(d.recibidoEl) : '—'}</td>
      <td className="px-2 py-1.5 text-xs whitespace-nowrap">{d.venceEl ? fecha(d.venceEl) : '—'}</td>
      <td className="px-2 py-1.5">
        {situacion ? (
          <FichaDeSituacion situacion={situacion} />
        ) : (
          <span className="text-xs text-apagado">
            {d.tipoId !== null && d.venceEl && !d.avisa ? tr('Historial', 'History') : ''}
          </span>
        )}
      </td>
    </tr>
  );
}
